import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { LMFieldBaseComponent, LMFilterOperatorEvent } from '@field/lm-field-base.component';
import { LMModel } from '@form/model';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { LMThemeService } from '@banner/lm-theme.service';
import { AppConstants } from '@utils/app.constants';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export class LMTextEvent implements LMFieldEvent {
  layoutObject: LMButtonLayout;
  event: Event;
  eventType: LMFieldEventType;

  constructor(event: Event, layoutObject: LMButtonLayout) {
    this.eventType = 'TextLinkEvent';
    this.event = event;
    this.layoutObject = layoutObject;
  }
}

enum LMTextTypeDisplayEnum {
  PLAIN = 0,
  MASK,
  PLAIN_FORM_TEXT,
  HTTP_FORM_TEXT,
  DISPLAY_AS_TEXT,
  DISPLAY_AS_TAG
}

@Component({
  selector:    'lm-text',
  templateUrl: './lm-text.component.html',
  styleUrls:   [ 'lm-text.component.scss' ]
})
export class LMTextComponent extends LMFieldBaseComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() layoutObject: LMFormLayoutField;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @Output() fieldEvent: EventEmitter<LMFieldEvent> = new EventEmitter<LMFieldEvent>();

  @HostBinding('attr.type') get isPassword() {
    return (this.field.type === 'password') ? 'password' : 'text';
  }

  @HostBinding('attr.data-automation-id') get dataFieldAutomationId() {
    return `lm-${this.fieldAutomationId}-field`;
  }

  @HostBinding('class.lm-form-text-container') get hasFormTextChild() {
    return this.isFormText;
  }

  public hasRichTextVars = false;
  public filterSettings: Array<SohoFieldFilterOption>;
  public launchButtonTooltip: string;
  public hasContextMenu$: Observable<boolean>;
  public messageIcon: string = null;
  public iconUrl: string;
  public validSohoIcon: boolean;

  public displayType: LMTextTypeDisplayEnum;
  public layoutDisplayType = LMTextTypeDisplayEnum;

  private useNgModelChangedForMask = false;
  private ngModelChangedValue = undefined;

  constructor(
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    public elementRef: ElementRef,
    public lmThemeService: LMThemeService
  ) {
    super(lmFieldServiceAdapter);
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    if (!this.field.displayAsText) {
      this.hasContextMenu$ = of(this.insertContextMenu && !(this.isFormText && !this.field.hasHttpUrl));
    } else {
      this.hasContextMenu$ = this.model.modelChanged.pipe(
        startWith(!this.modelField.isBlank),
        map(() => !this.modelField.isBlank && this.insertContextMenu)
      );
    }

    this.launchButtonTooltip = Soho.Locale.translate('Launch');

    if (this.specField) {
      this.setNumberFormat();
      this.setSearchFieldNumberFormat();
      this.setSize();
      this.setFieldClasses();
      this.setType();
    }

    if (this.layoutObject.icon) {
      this.validSohoIcon = this.lmFieldServiceAdapter.lmThemeService.isValidSohoIcon(this.layoutObject.icon);

      this.messageIcon = this.layoutObject.icon;
      this.iconUrl = this.layoutObject.iconUrl;
    }

    if (!(this.displayAsText || this.displayAsTag || this.isFormText)) {
      this.subscribeToInlineError();
    }

    if (this.field.text && this.field.text.hasOwnProperty('hasRichTextVars')) {
      this.hasRichTextVars = true;
    }

    if (!this.displayAsText && !this.displayAsTag && !this.isFormText && !this.hasMask) {
      this.displayType = LMTextTypeDisplayEnum.PLAIN;

    } else if (!this.displayAsText && !this.displayAsTag && !this.isFormText && this.hasMask) {
      this.displayType = LMTextTypeDisplayEnum.MASK;
      this.useNgModelChangedForMask = true;
      this.modelField.filterType = this.modelField?.filterCriteria?.operator || 'equals';

    } else if (this.isFormText) {
      if (this.field.hasHttpUrl || this.layoutObject.linkNavigation) {
        this.displayType = LMTextTypeDisplayEnum.HTTP_FORM_TEXT;

      } else {
        this.displayType = LMTextTypeDisplayEnum.PLAIN_FORM_TEXT;
      }

    } else if (this.displayAsTag && !this.isCompoundFieldMember) {
      this.displayType = LMTextTypeDisplayEnum.DISPLAY_AS_TAG;

    } else if (this.displayAsText && !this.isCompoundFieldMember) {
      this.displayType = LMTextTypeDisplayEnum.DISPLAY_AS_TEXT;
    }

    this.setDefaultFieldStyle();

    if (this.layoutObject.textSearchable) {
      // There is an issue with SQL server that causes text searchable fields
      // to be starts with in the back end. However, we still want to send
      // the filterType back as contains in this case, so we have the correct
      // * on the values in the back end, and not use a like query. Ask
      // Joe Schreier for more information. This may be updated after
      // https://jira.lawson.com/browse/LMRK-113234 is compelete
      this.filterSettings = this.textSearchableSettings;
      this.modelField.filterType = this.modelField?.filterCriteria?.operator || 'contains';
    } else {
      this.filterSettings = this.alphaFilterSettings;
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public onBlur(event) {
    // if not onSearchForm and hasFieldChanged, then field changed while it had focus - update the model
    // or is onSearchForm - update the model
    if ((!this.onSearchForm && this.hasFieldChanged) || this.onSearchForm) {

      // used to transform BCD and PERCENT types so they have trailing zeros before leaving the field
      this.updateModelField();
    }

    super.onBlur(event);
  }

  public onSearchKeypress(event) {
    // listen for enter key
    if (event.which === 13 && event.target.nodeName !== 'BUTTON') {

      // used to transform and store the BCD or PERCENT value before the onSearchClicked() event is executed
      this.updateModelField();
    }
  }

  public onNgModelChange(value) {
    // Fields need to format the value according to the type. For example, any trailing zeros are displayed or uppercase
    // place value into temporary location until the blur event is executed
    // value is then transformed into data format and placed into modelField.value
    this.ngModelChangedValue = value;
  }

  onFiltered(event: SohoFieldFilteredEvent) {
    super.onFiltered(event);
    if (this.onSearchForm) {
      this.updateModelField();
    }
  }


  contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  }

  openCompactDialog(event) {
    this.onContextMenuEvent({ args: [ event.currentTarget ] });
  }

  private setDefaultFieldStyle() {
    if (!this.isCompoundFieldMember
      && (this.displayType === LMTextTypeDisplayEnum.DISPLAY_AS_TAG
        || this.displayType === LMTextTypeDisplayEnum.DISPLAY_AS_TEXT)) {

      if (this.specField && (this.specField.type === AppConstants.NUMERIC
        || this.specField.type === AppConstants.BCD || this.specField.type === AppConstants.PERCENT)) {
        if (!this.layoutObject.fieldStyle) {
          this.layoutObject.fieldStyle = {};
        }

        // force display as text or display as tag number fields to default left align
        if (!this.layoutObject.fieldStyle.align) {
          this.layoutObject.fieldStyle.align = 'left';
        }
      }
    }
  }

  private updateModelField() {
    if (this.ngModelChangedValue !== undefined) {
      let value = this.ngModelChangedValue;
      if (this.isUpperCase) {
        value = this.ngModelChangedValue.toUpperCase();
      }

      if (this.transformOptions.allowMultiSelections && this.onSearchForm) {
        const allowZero = this.specField.type === AppConstants.BCD ||
                          this.specField.type === AppConstants.PERCENT ||
                          this.specField.type === AppConstants.NUMERIC;
        const multiValue = this.lmFieldServiceAdapter.lmFormatUtilService.getMultipleFilterFieldValue(value, this.specField.type, this.specField.decSize, this.modelField.precision, allowZero);
        this.modelField.enteredFilterValue = multiValue.enteredFilterValue;
        this.modelField.value = multiValue.formattedFilterValue;
      } else {
        this.modelField.toDataValue(value, this.transformOptions);
        this.ngModelChangedValue = undefined;
      }

      // tell the control to check the dirty state
      if (!this.onSearchForm) {
        this.checkApplyState = true;
        this.runApplyState = true;
      }
    }
  }
}
